import { Component, inject, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { Router } from '@angular/router';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoginService } from '@/services/login.service';
import { MessageService } from 'primeng/api';
import { Toast, ToastModule } from 'primeng/toast';
import { UserService } from '@/services/user.service';
import { DialogModule } from 'primeng/dialog';
import { InputOtpModule } from 'primeng/inputotp';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ChangeDetectorRef } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ClientService } from '@/services/client.service';
// Login request and response interfaces
interface LoginRequest {
    email: string;
    password: string;
    rememberMe?: boolean;
}

interface LoginResponse {
    success: boolean;
    access_token?: string;
    refreshToken?: string;
    user?: {
        id: string;
        username: string;
        email: string;
        roles: string[];
    };
    message?: string;
    expiresIn?: number;
}

@Component({
    selector: 'app-idp-login',
    imports: [CommonModule, ReactiveFormsModule, CardModule, ButtonModule, InputTextModule, PasswordModule, CheckboxModule, DividerModule, MessageModule, ToastModule, DialogModule, InputOtpModule, FormsModule, FloatLabelModule],
    templateUrl: './idp-login.component.html',
    styleUrl: './idp-login.component.scss'
})
export class IdpLoginComponent implements OnInit, AfterViewInit {
    private http = inject(HttpClient);
    private router = inject(Router);
    private loginService = inject(LoginService);
    private messageService = inject(MessageService);
    private userService = inject(UserService);
    private fb = inject(FormBuilder);

    // Reactive Form
    loginForm!: FormGroup;
    closeResetdialog: boolean = false;
    resetPasswordForm!: FormGroup;
    // UI state
    isLoading: boolean = false;
    errorMessage: string = '';

    /** Two-step login: collect the email first, then the password. */
    step: 'email' | 'password' = 'email';

    // Getters for form controls
    get usernameControl(): AbstractControl {
        return this.loginForm.get('username')!;
    }

    get passwordControl(): AbstractControl {
        return this.loginForm.get('password')!;
    }

    forgotVisible: boolean = false;
    forgotEmail: string = '';
    otpFields = ['', '', '', '', '', ''];
    otp: string = '';
    resendTimer = 180; // 3 minutes
    timerInterval: any;
    //otpModes
    otpMode = false;
    resetPasswordMode = false;

    newPassword = '';
    confirmPassword = '';

    // API configuration
    private readonly API_BASE_URL = environment.apiUrl;
    private readonly LOGIN_ENDPOINT = '/token';

    constructor(
        private cdr: ChangeDetectorRef,
        private clientService: ClientService
    ) {}

    /**
     * Single submit handler for both steps: step 1 only validates the email and
     * moves on, step 2 performs the actual login.
     */
    onContinue() {
        if (this.step === 'email') {
            this.usernameControl.markAsTouched();
            if (this.usernameControl.invalid) {
                return;
            }
            this.step = 'password';
            this.focusControl('password');
            return;
        }

        this.passwordControl.markAsTouched();
        this.onLogin();
    }

    /** Back to step 1 — the password is discarded so it is never carried over. */
    onEditEmail() {
        this.step = 'email';
        this.errorMessage = '';
        this.passwordControl.reset();
        this.focusControl('username');
    }

    private focusControl(id: string) {
        setTimeout(() => document.getElementById(id)?.focus(), 0);
    }

    onLogin() {
        if (this.loginForm.invalid) {
            this.errorMessage = 'Please enter valid email and password';
            return;
        }

        // Clear any previous error messages
        this.errorMessage = '';
        this.isLoading = true;

        // Prepare login request
        const loginRequest: LoginRequest = {
            email: this.loginForm.value.username.trim(),
            password: this.loginForm.value.password
            //rememberMe: this.rememberMe
        };

        // Set HTTP headers
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });

        this.loginService.login(loginRequest).subscribe({
            next: (res: any) => {
                this.loginService.setAccessToken(res.access);
                this.loginService.setRefreshsToken(res.refresh);

                // Load user info and wait for it to complete before redirecting
                const userInfo = localStorage.getItem('userInfo');

                if (!userInfo) {
                    // If userInfo doesn't exist, fetch it first
                    this.userService.getUserShortInfo().subscribe({
                        next: (userInfoRes: any) => {
                            localStorage.setItem('userInfo', JSON.stringify(userInfoRes));
                            // Refresh privileges in service after setting userInfo
                            this.loginService.setUserPrivileges(userInfoRes);
                            this.isLoading = false;
                            this.redirectBasedOnRole();
                        },
                        error: (err: any) => {
                            console.error('Failed to load user info:', err);
                            this.isLoading = false;
                            // Redirect to default page if userInfo fetch fails
                            this.router.navigate(['/home']);
                        }
                    });
                } else {
                    // If userInfo exists, redirect immediately
                    const userData = JSON.parse(userInfo);
                    this.loginService.setUserPrivileges(userData);
                    this.isLoading = false;
                    this.redirectBasedOnRole();
                }

                // Load users list in background
                this.userService.checkUSerInfoAndLoad();
            },
            error: (error: any) => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error.message || 'Something went wrong, please try again.' });
            }
        });
    }

    onForgotPassword() {
        this.forgotVisible = true;
        // Reuse the address already entered in step 1 so the user need not retype it.
        this.forgotEmail = this.usernameControl.value?.trim() || '';
        this.otpFields = ['', '', '', '', '', ''];
        this.otp = '';
        this.newPassword = '';
        this.confirmPassword = '';
    }

    isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    onSignUp() {}

    // SSO Login methods
    onMicrosoftSSO() {}

    onGoogleSSO() {}

    // Helper method to clear stored authentication data
    clearAuthData() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_info');
        localStorage.removeItem('token_expiration');
    }

    // Method to check if user is already logged in
    checkExistingAuth() {
        const token = localStorage.getItem('auth_token');
        const expiration = localStorage.getItem('token_expiration');

        if (token && expiration) {
            const expirationTime = parseInt(expiration);
            const currentTime = new Date().getTime();

            if (currentTime < expirationTime) {
                // User is still logged in, redirect to dashboard
                this.router.navigate(['/home']);
            } else {
                // Token expired, clear auth data
                this.clearAuthData();
            }
        }
    }
    submitForgotPassword() {}
    sendOTP() {
        this.loginService.sendOtp(this.forgotEmail).subscribe({
            next: (res) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'OTP Sent',
                    detail: 'OTP has been sent to your email successfully.'
                });
                this.otpMode = true;
                this.startResendTimer();
            },
            error: (err) => {
                console.error(err);

                this.messageService.add({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Something went wrong. Failed to send OTP.'
                });
            }
        });
    }

    ngOnInit() {
        // Initialize reactive form
        this.loginForm = this.fb.group({
            username: [null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
            password: [null, Validators.required]
        });

        this.intializeResetPasswordForm();

        // Listen to form changes to handle auto-fill validation and clear error messages
        this.loginForm.valueChanges.subscribe(() => {
            if (this.loginForm.value.username && this.loginForm.value.password) {
                this.loginForm.updateValueAndValidity();
            }
            // Clear error message when user starts typing
            if (this.errorMessage) {
                this.errorMessage = '';
            }
        });

        //Check if already logged in via LoginService
        if (this.loginService.isAuthenticated()) {
            this.redirectBasedOnRole();
        } else {
        }
    }

    private intializeResetPasswordForm() {
        this.resetPasswordForm = this.fb.group(
            {
                newPassword: [null, [Validators.required, this.passwordStrengthValidator]],
                confirmPassword: [null, Validators.required]
            },
            { validators: this.passwordsMatchValidator }
        );
    }

    ngAfterViewInit() {
        // Trigger validation after browser auto-fill
        setTimeout(() => {
            this.loginForm.updateValueAndValidity(); // Mark controls as touched if they have values (for auto-fill)
            if (this.usernameControl.value) {
                this.usernameControl.markAsTouched();
            }
            if (this.passwordControl.value) {
                this.passwordControl.markAsTouched();
            }
            // Land the caret in the email field so step 1 is ready to type into.
            document.getElementById('username')?.focus();
        }, 0);
    }

    /**
     * Redirect user based on their role and is_superuser status
     */
    private redirectBasedOnRole(): void {
        const userInfoStr = localStorage.getItem('userInfo');
        if (!userInfoStr) {
            this.router.navigate(['/home']);
            return;
        }

        try {
            const userInfo = JSON.parse(userInfoStr);
            const { user_type, client_id, customer_id } = userInfo;
            let normalizedType = '';
            if (typeof user_type === 'string') {
                normalizedType = user_type.trim().toUpperCase();
            } else if (typeof user_type === 'number') {
                normalizedType = user_type === 5 ? 'COZENTUS_USER' : user_type === 10 ? 'CLIENT_USER' : '';
            }

            if (normalizedType === 'COZENTUS_USER') {
                this.router.navigate(['/home']);
                return;
            }
            if (normalizedType === 'CLIENT_USER' && client_id && client_id.length > 0) {
                // this.router.navigate(['/extraction/idp-application', client_id[0]]);
                this.clientService.setClientId(client_id[0]);
                this.router.navigate(['home/extraction/idp-doc-grid', client_id[0]]);
                return;
            }
            this.router.navigate(['/home']);
        } catch (error) {
            console.error('Error parsing user info:', error);
            this.router.navigate(['/home']);
        }
    }

    startResendTimer() {
        this.resendTimer = 180;

        this.timerInterval = setInterval(() => {
            this.resendTimer--;
            if (this.resendTimer === 0) {
                clearInterval(this.timerInterval);
            }
        }, 1000);
    }
    onOtpInput(event: any, index: number) {
        const value = event.target.value;

        // Allow only 0-9
        if (!/^[0-9]$/.test(value)) {
            event.target.value = '';
            return;
        }

        // Update field
        this.otpFields[index] = value;

        // Auto-focus next box
        if (value && index < 5) {
            const next = document.getElementById('otp-' + (index + 1)) as HTMLElement;
            next?.focus();
        }

        // Join digits to a single OTP string
        this.otp = this.otpFields.join('');
        
    }
    verifyOTP() {
        // 1️⃣ Validate OTP length
        if (!this.otp || this.otp.toString().length !== 6) {
            this.messageService.add({
                severity: 'error',
                summary: 'Invalid OTP',
                detail: 'Please enter a valid 6-digit OTP.'
            });
            return;
        }

        // 2️⃣ API Call
        this.loginService.verifyOtp(this.forgotEmail, this.otp).subscribe({
            next: (res) => {
                // SUCCESS TOAST
                this.messageService.add({
                    severity: 'success',
                    summary: 'OTP Verified',
                    detail: 'OTP verified successfully!'
                });

                // 3️⃣ Switch to Reset Password Screen
                this.otpMode = false;
                this.resetPasswordMode = true;

                // 4️⃣ Fix ghost modal & force dialog to resize
                setTimeout(() => this.cdr.detectChanges(), 50);
            },

            error: (err) => {
                // ERROR TOAST
                this.messageService.add({
                    severity: 'error',
                    summary: 'Verification Failed',
                    detail: err.error?.message || 'Invalid OTP, please try again.'
                });
            }
        });
    }

    // resetPASSWORD() {
    //     if (!this.newPassword || !this.confirmPassword) {
    //         this.messageService.add({
    //             severity: 'error',
    //             summary: 'Required',
    //             detail: 'Both password fields are required.'
    //         });
    //         return;
    //     }

    //     if (this.newPassword !== this.confirmPassword) {
    //         this.messageService.add({
    //             severity: 'error',
    //             summary: 'Mismatch',
    //             detail: 'Passwords do not match!'
    //         });
    //         return;
    //     }

    //     this.loginService.resetPassword(this.forgotEmail, this.newPassword).subscribe({
    //         next: (res) => {
    //             this.messageService.add({
    //                 severity: 'success',
    //                 summary: 'Password Reset',
    //                 detail: 'Your password has been reset successfully.'
    //             });

    //             // Close dialog after success
    //             setTimeout(() => {
    //                 this.forgotVisible = false;
    //                 this.resetPasswordMode = false;
    //                 this.otpMode = false;
    //             }, 1500);
    //         },
    //         error: (err) => {
    //             this.messageService.add({
    //                 severity: 'error',
    //                 summary: 'Failed',
    //                 detail: err.error?.message || 'Failed to reset password.'
    //             });
    //         }
    //     });
    // }

    resetPASSWORD() {
        if (this.resetPasswordForm.invalid) {
            this.resetPasswordForm.markAllAsTouched();
            return;
        }

        const password = this.resetPasswordForm.value.newPassword;

        this.loginService.resetPassword(this.forgotEmail, password).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Password Reset',
                    detail: 'Your password has been reset successfully.'
                });

                setTimeout(() => {
                    this.forgotVisible = false;
                    this.resetPasswordMode = false;
                    this.otpMode = false;
                    this.resetPasswordForm.reset();
                }, 1500);
            },
            error: (err) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Failed',
                    detail: err.error?.message || 'Failed to reset password.'
                });
            }
        });
    }
    closeResetDialog() {
        this.forgotVisible = false;
        this.otpMode = false;
        this.resetPasswordMode = false;
        this.resetPasswordForm.reset();
    }

    passwordStrengthValidator(control: AbstractControl) {
        const value = control.value || '';

        const errors: any = {};

        if (!/[A-Z]/.test(value)) {
            errors.uppercase = 'At least one uppercase letter required';
        }
        if (!/[a-z]/.test(value)) {
            errors.lowercase = 'At least one lowercase letter required';
        }
        if (!/[0-9]/.test(value)) {
            errors.number = 'At least one number required';
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
            errors.special = 'At least one special character required';
        }
        if (value.length < 8) {
            errors.length = 'Minimum 8 characters required';
        }

        return Object.keys(errors).length ? errors : null;
    }

    passwordsMatchValidator(formGroup: FormGroup) {
        const newPassword = formGroup.get('newPassword')?.value;
        const confirmPassword = formGroup.get('confirmPassword')?.value;
        return newPassword === confirmPassword ? null : { passwordMismatch: true };
    }
}
